import request, { Response } from "superagent";

import config from "config/config";
import { getTabToken } from "common/MetaActions";
import { UserForm } from "models/admin/admin";
import { ExternalUserPayload } from "helpers/WizardHelper";
import { useRootStore } from "store";
import { ExternalUser } from "models/customers/customers";

export class UsersAPI {
  static async searchExternalUsers(query: any): Promise<Response> {
    return new Promise((resolve, reject) => {
      request
        .get(config.api.user.find)
        .set("Accept", "application/json")
        .set("Authorization", "Bearer " + getTabToken())
        .query({ find: query })
        .end((err, res) => {
          err ? reject(err) : resolve(res);
        });
    });
  }

  static async searchInternalUsers(query: any): Promise<Response> {
    return new Promise((resolve, reject) => {
      request
        .get(config.api.user.internalUsers)
        .set("Accept", "application/json")
        .set("Authorization", "Bearer " + getTabToken())
        .query({ find: query })
        .end((err, res) => {
          err ? reject(err) : resolve(res);
        });
    });
  }

  static async editExternalUser(user: UserForm) {
    return new Promise((resolve, reject) => {
      request
        .patch(config.api.user.adminUser.editExternal)
        .set("Accept", "application/json")
        .set("Authorization", "Bearer " + getTabToken())
        .send(user)
        .end((err, res) => {
          err ? reject({ err, res }) : resolve(res);
        });
    });
  }

  static async sendActivation(email: string) {
    return new Promise((resolve, reject) => {
      request
        .post(config.api.user.resetPassword)
        .set("Accept", "application/json")
        .send({
          email: email
        })
        .end((err, res) => {
          err
            ? reject({
                err,
                res
              })
            : resolve(res);
        });
    });
  }

  static async fetchInternalUserLogin(code: string | null) {
    const response = await request
      .post(config.api.user.internalUserLogin)
      .set("Accept", "application/json")
      .send({
        AzureAdRedirectUrl:
          window.location.origin + "/authenticate-internal-user",
        AzureCode: code
      });
    return response.body;
  }

  static async fetchUserLogin({
    code,
    password,
    username
  }: {
    code: string;
    password: string;
    username: string;
  }) {
    const response = await request
      .post(config.api.user.login)
      .set("Accept", "application/json")
      .send({
        AzureAdRedirectUrl:
          window.location.origin + "/authenticate-internal-user",
        AzureCode: code,
        UserId: username,
        Password: password
      });
    return response.body;
  }

  static async validateToken(token: string) {
    const response = await request.get(config.api.user.validateToken(token));
    const validToken: string = response.body;
    return validToken;
  }

  static async setPasswordAndGetAuthFromKey(key: string, password: string) {
    const response = await request
      .put(config.api.user.activateUser)
      .set("Accept", "application/json")
      .send({ key, password });
    return response.body;
  }

  static async generateOnetimeLogin(email: string) {
    return await request.post(config.api.user.generateOnetimeLogin).send({
      UserEmail: email
    });
  }

  static async onetimeLogin(onetimeLoginGuid: string) {
    const response = await request
      .post(config.api.user.onetimeLogin)
      .set("Accept", "application/json")
      .send({
        OnetimeLoginGuid: onetimeLoginGuid
      });
    return response.body;
  }

  static async validateResetPasswordToken(token: string) {
    return await request.get(config.api.user.isTokenValid(token));
  }

  static async sendSetNewPassword(password: string, key: string) {
    const response = await request
      .put(config.api.user.setPassword)
      .set("Accept", "application/json")
      .send({ password, key });
    return response.body;
  }

  static async sendResetPassword(email: string) {
    return await request
      .post(config.api.user.resetPassword)
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken())
      .send({
        email: email
      });
  }

  static async saveCustomerExternalUserSettings(
    id: string,
    externalUsers: ExternalUserPayload[]
  ) {
    const { user } = useRootStore.getState();
    return await request
      .put(config.api.user.externalUsers.put())
      .set("Authorization", `Bearer ${user.token}`)
      .send({
        CustomerId: id,
        ExternalUsers: externalUsers
      });
  }

  static async addNewExternalUserToCustomer(
    customerId: string,
    user: Partial<ExternalUser>
  ) {
    const { token } = useRootStore.getState().user;
    const response = await request
      .post(config.api.user.addNewExternalUserToCustomer(customerId))
      .set("Authorization", `Bearer ${token}`)
      .send({
        ...user
      });
    return response.body;
  }
}
